import * as _ from 'underscore';

import { ImageDisplayElementApiModel } from '../../../api/pagebuilder/models/image-display-element-api.model';
import { ReleaseStatus } from '../../../api/wire/enums/release-status.enum';
import { WireRegion } from '../../../api/wire/enums/wire-region.enum';
import { NewswireApiModel } from '../../../api/wire/models/newswire-api.model';
import { BaseModel } from '../../../Common/Basic/models/base.model';
import { DeliverySchedule, ScheduleType } from '../../../Common/Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { UserNoteModel } from '../../../Common/Misc/models/user-note.model';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { ImageDisplayElementModel } from '../../../Common/PageBuilder/models/image-display-element.model';
import { RichTextDisplayElementModel } from '../../../Common/PageBuilder/models/rich-text-display-element.model';
import { VideoDisplayElementModel } from '../../../Common/PageBuilder/models/video-display-element.model';
import {
    WireBlockTypeApiEnum,
    wireBlockTypeApiEnumToWireBlockTypeEnum,
    WireBlockTypeEnum,
    wireBlockTypeEnumToWireBlockTypeApiEnum
} from '../enums/wire-block-type.enum';
import { ContactDisplayElementApiModel } from './contact-display-element-api.model';
import { ContactDisplayElementModel } from './contact-display-element.model';
import { SocialLinkDisplayElementApiModel } from './social-link-display-element-api.model';
import { SocialLinkDisplayElementModel } from './social-link-display-element.model';
import { WireBlockApiModel } from './wire-block-api.model';
import { wireBlockTypeEnumToBlockTypeMap } from './wire-block-types.model';
import { WireCompanyAddressModel } from './wire-company-address.model';
import { WireCompanyBioApiModel } from './wire-company-bio-api.model';
import { WireCompanyBioModel } from './wire-company-bio.model';
import { WireCompanyLogoModel } from './wire-company-logo.model';
import { WireContactModel } from './wire-contact.model';
import { WireImageDisplayElementApiModel } from './wire-image-display-element-api.model';
import { WireTextDisplayElementApiModel } from './wire-text-display-element-api.model';
import { WireVideoDisplayElementApiModel } from './wire-video-display-element-api.model';

export class WireModel extends BaseModel<WireModel, NewswireApiModel>{

    status: ReleaseStatus = ReleaseStatus.draft;
    hidden: boolean;
    groupId: number;
    isFavorite: boolean;

    deliverySchedule: DeliverySchedule = new DeliverySchedule(null, null);

    blocks: BlockModel[] = [];

    createdByUserId?: number;
    createdDate?: Date;
    modifiedByUserId?: number;
    modifiedDate?: Date;
    publishedUserId?: number;
    newsDirectPublicationDate: Date;
    bodyHtml?: string = '';

    regions: WireRegion[] = [];
    seoKeywords: string[] = [];

    contactName: string;
    contactEmail: string;

    userNotes: UserNoteModel[];

    headline: string;
    subHeadline: string;
    dateline: string;
    companyName: string;

    companyAddress: WireCompanyAddressModel = null;
    companyLogo: WireCompanyLogoModel = null;

    simpleCompareFields = [
        'name',
        'contactName',
        'contactEmail',
        'subHeadline',
        'dateline',
        'companyName'
    ];

    arrayCompareFields = [
        'userNotes'
    ];
    /* companyName: string;
    companyLogoUrl: string;
    dateLineLocation: string;
    dateLineDate: number;

    publicContactName: string;
    publicContactOrganization: string;
    publicContactPhone: string;
    publicContactEmail: string;
    publicContactFacebook: string;
    publicContactTwitter: string;
    publicContactLinkedIn: string;

    categories: WireCategoriesEnum[];

    // @TODO: File object?
    fileAttachments: string[];

    metadataDescription: string;
    distributionRegions: number[];

    publishedUrl: string;

    contactInformationName: string;
    contactInformationPhoneNumber: string;
    contactInformationEmailAddress: string;

    preferredMethodOfContact: WireContactsEnum;
    publishImmediately: boolean;
    copyApprovalRequired: boolean;

    notesForTheEditor: string;*/

    constructor(apiModel?: NewswireApiModel) {
        super();
        if (apiModel) {
            this.id = apiModel.id;
            this.name = apiModel.name;
            this.hidden = apiModel.hidden;
            this.status = <ReleaseStatus> apiModel.status;
            this.deliverySchedule = new DeliverySchedule(
                apiModel.scheduledDate,
                !!apiModel.timeZone ? apiModel.timeZone : null
            );

            if (apiModel.blocks) {
                this.mapBlocksFromApi(apiModel.blocks);
            }

            this.createdDate = apiModel.createdDate;
            this.createdByUserId = apiModel.createdUserId;
            this.modifiedDate = apiModel.modifiedDate;
            this.modifiedByUserId = apiModel.modifiedUserId;
            this.publishedUserId = apiModel.publishedUserId;
            this.newsDirectPublicationDate = apiModel.newsDirectPublicationDate;
            this.bodyHtml = apiModel.bodyHtml;
            this.seoKeywords = apiModel.seoKeywords;
            this.contactName = apiModel.contactName;
            this.contactEmail = apiModel.contactEmail;
            this.regions = apiModel.wireRegions;
            this.userNotes = apiModel.userNotes?.map(note => new UserNoteModel(note));
            this.headline = apiModel.headline;
            this.subHeadline = apiModel.subHeadline;
            this.dateline = apiModel.dateline;
            this.companyName = apiModel.companyName;
            // For companyAddress, companyLogo, null means related blocks are not added in center panel
            this.companyAddress = apiModel.companyAddress ? new WireCompanyAddressModel(apiModel.companyAddress) : null;
            this.companyLogo = apiModel.companyLogo ? new WireCompanyLogoModel(apiModel.companyLogo) : null;
            this.groupId = apiModel.displayGroupId;
            this.isFavorite = apiModel.isFavorited;
        }
    }

    constructFromApiModel(apiModel: NewswireApiModel): WireModel{
        return new WireModel(apiModel);
    }

    toApiModel() {
        const apiModel: NewswireApiModel = {
            id: this.id,
            name: this.name?.trim(),
            status: this.status,
            // generalStyles: this.generalStyles ? JSON.stringify(this.generalStyles.toApi()) : '',
            timeZone: this.deliverySchedule.timeZone,
            createdDate: this.createdDate,
            createdUserId: this.createdByUserId,
            modifiedDate: this.modifiedDate,
            modifiedUserId: this.modifiedByUserId,
            publishedUserId: this.publishedUserId,
            newsDirectPublicationDate: this.newsDirectPublicationDate,
            bodyHtml: this.bodyHtml,
            contactName: this.contactName,
            contactEmail: this.contactEmail,
            wireRegions: this.regions,
            seoKeywords: this.seoKeywords?.slice(),
            userNotes: this.userNotes?.map(note => note.toApiModel()),
            headline: this.headline,
            hidden: this.hidden,
            subHeadline: this.subHeadline,
            dateline: this.dateline,
            companyName: this.companyName,
            companyAddress: this.companyAddress ? this.companyAddress.toApiModel() : null,
            companyLogo: this.companyLogo ? this.companyLogo.toApiModel() : null,
            // sentNow: this.sentNow,
            // sentDate: this.sentDate,
            // sentUserId: this.sentUserId,
            // recipientEmails: this.recipientInformation ? this.recipientInformation.getEmailRecipients() : [],
            // recipients: this.recipientInformation ? this.recipientInformation.toApiRecipientModel() : [],
            // attachments: [...this.attachments],
            // confirmationRecipients: [...this.confirmationRecipients],
            scheduledDate: this.deliverySchedule.scheduleType === ScheduleType.immediate
                ? null
                : this.deliverySchedule.scheduledDateUTC,
            displayGroupId: this.groupId,
            isFavorited: this.isFavorite
        };

        this.setBlocks(apiModel);
        return apiModel;
    }

    mapBlocksFromApi(apiBlocks: WireBlockApiModel[] = []): void {
        const blocks = [];

        for (const apiBlock of apiBlocks) {
            let block;
            const blockType = wireBlockTypeEnumToBlockTypeMap.get(wireBlockTypeApiEnumToWireBlockTypeEnum.get(apiBlock.type));

            switch (blockType.key) {
                case WireBlockTypeEnum.text:
                    const textApiData: WireTextDisplayElementApiModel = apiBlock as WireTextDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new RichTextDisplayElementModel(textApiData.text, textApiData.styles)
                    );

                    const stylesObject = JSON.parse(textApiData.styles);
                    block.displayElement.htmlEdit = stylesObject['htmlEdit'];
                    block.displayElement.enablePersonalizations = false;
                    block.displayElement.enableLineHeight = false;
                    block.displayElement.enableFontColor = false;
                    block.displayElement.enableBackgroundColor = false;
                    block.displayElement.enableFontFamily = false;
                    block.displayElement.enableFontSize = false;
                    block.displayElement.enableInsertImage = false;
                    block.displayElement.enableHtmlEditor = false;
                    block.displayElement.enableStrikethrough = false;
                    block.displayElement.enableNumberedList = false;
                    block.displayElement.enableBlockQuote = false;
                    block.displayElement.allowImage = false;
                    block.displayElement.pastePlainText = true;
                    block.displayElement.enableAIHelper = true;

                    break;

                case WireBlockTypeApiEnum.image:
                    const imageApiData: ImageDisplayElementApiModel = apiBlock as WireImageDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new ImageDisplayElementModel(imageApiData)
                    );

                    break;
                case WireBlockTypeApiEnum.contact:
                    const contactApiData: ContactDisplayElementApiModel = apiBlock as ContactDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new ContactDisplayElementModel(new WireContactModel({
                            name: contactApiData.name,
                            email: contactApiData.email,
                            contactImageUrl: contactApiData.contactImageUrl,
                            title: contactApiData.title,
                            primaryPhone: contactApiData.primaryPhone,
                            secondaryPhone: contactApiData.secondaryPhone
                        } as WireContactModel))
                    );

                    break;
                case WireBlockTypeApiEnum.video:
                    const videoApiData: WireVideoDisplayElementApiModel = apiBlock as WireVideoDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new VideoDisplayElementModel(videoApiData)
                    );

                    break;

                case WireBlockTypeApiEnum.links:
                    const linksApiData: SocialLinkDisplayElementApiModel = apiBlock as SocialLinkDisplayElementApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new SocialLinkDisplayElementModel(linksApiData)
                    );

                    break;

                case WireBlockTypeEnum.companyBio:
                    const companyBioApiData: WireCompanyBioApiModel = apiBlock as WireCompanyBioApiModel;
                    block = new BlockModel(
                        null,
                        blockType,
                        new WireCompanyBioModel(companyBioApiData)
                    );

                    break;
            }

            if (apiBlock.styles) {
                const styles = JSON.parse(apiBlock.styles);
                if (styles) {
                    // @ TODO - NEP-3392 hack should add isBoundToGlobalStyles to
                    block.displayElement.isBoundToGlobalStyles = styles.isBoundToGlobalStyles === true || styles.isBoundToGlobalStyles === 'true';
                } else {
                    block.displayElement.isBoundToGlobalStyles = true;
                }
            } else {
                block.displayElement.isBoundToGlobalStyles = true;
            }

            blocks.push(block);
        }

        this.blocks = blocks;
    }

    setBlocks(apiModel: NewswireApiModel) {
        const apiBlocks: WireBlockApiModel[] = [];

        for (const block of this.blocks) {
            if (!block.type || !block.type.key) {

                return;
            }

            let styles;

            switch (block.type.key) {
                case WireBlockTypeEnum.text:
                    const textDisplayElement = block.displayElement as RichTextDisplayElementModel;

                    // hack @TODO NEP-3392
                    styles = textDisplayElement.styles.toBlockStyle();
                    styles.isBoundToGlobalStyles = textDisplayElement.isBoundToGlobalStyles;
                    styles.htmlEdit = textDisplayElement.htmlEdit;
                    // endhack

                    apiBlocks.push({
                        type: wireBlockTypeEnumToWireBlockTypeApiEnum.get(block.type.key as WireBlockTypeEnum),
                        styles: JSON.stringify(styles),
                        text: textDisplayElement.text,
                        customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                    } as WireTextDisplayElementApiModel);

                    break;

                case WireBlockTypeEnum.image:
                    const imageDisplayElement = block.displayElement as ImageDisplayElementModel;

                    // hack @TODO NEP-3392
                    styles = imageDisplayElement.styles.toBlockStyle();
                    styles.isBoundToGlobalStyles = imageDisplayElement.isBoundToGlobalStyles;

                    const apiBlock = {
                        type: wireBlockTypeEnumToWireBlockTypeApiEnum.get(block.type.key as WireBlockTypeEnum),
                        styles: JSON.stringify(styles),
                        url: imageDisplayElement.image?.url ? imageDisplayElement.image.url : null,
                        width: imageDisplayElement.width,
                        customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                        link: imageDisplayElement.settings?.link?.toApi(),
                        displaySize: imageDisplayElement.settings?.displaySize,
                        // String values can't be empty, so we treat any empty as undefined
                        caption: imageDisplayElement.settings?.caption || null,
                        altText: imageDisplayElement.image?.altText || null,
                        // imageAlignment: imageDisplayElement.settings?.imageAlignment,
                    } as WireImageDisplayElementApiModel;

                    apiBlocks.push(apiBlock);

                    break;
                case WireBlockTypeEnum.video:
                    const videoDisplayElement = block.displayElement as VideoDisplayElementModel;
                    styles = videoDisplayElement.styles.toBlockStyle();
                    styles.isBoundToGlobalStyles = videoDisplayElement.isBoundToGlobalStyles;

                    const videoApiBlock = {
                        type: wireBlockTypeEnumToWireBlockTypeApiEnum.get(block.type.key as WireBlockTypeEnum),
                        styles: JSON.stringify(styles),
                        url: videoDisplayElement.url ? videoDisplayElement.url : null,
                        customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                        caption: videoDisplayElement.caption,
                    } as WireVideoDisplayElementApiModel;

                    apiBlocks.push(videoApiBlock);

                    break;
                case WireBlockTypeEnum.contact:
                    const contactDisplayElement = block.displayElement as ContactDisplayElementModel;

                    styles = contactDisplayElement.styles.toBlockStyle();
                    styles.isBoundToGlobalStyles = contactDisplayElement.isBoundToGlobalStyles;

                    const contactApiBlock = {
                        type: wireBlockTypeEnumToWireBlockTypeApiEnum.get(block.type.key as WireBlockTypeEnum),
                        ...contactDisplayElement.contact.toApiModel()
                    } as ContactDisplayElementApiModel;

                    apiBlocks.push(contactApiBlock);

                    break;

                case WireBlockTypeEnum.links:
                    const socialLinkDisplayElement = block.displayElement as SocialLinkDisplayElementModel;

                    const socialLinkApiBlock = {
                        type: wireBlockTypeEnumToWireBlockTypeApiEnum.get(block.type.key as WireBlockTypeEnum),
                        styles: JSON.stringify(socialLinkDisplayElement.styles?.toApi()),
                        socialLinks: socialLinkDisplayElement.socialLinks.map(link => link.toApiModel())
                    } as SocialLinkDisplayElementApiModel;

                    apiBlocks.push(socialLinkApiBlock);

                    break;

                case WireBlockTypeEnum.companyBio:
                    const companyBioDisplayElement = block.displayElement as WireCompanyBioModel;

                    const companyBioApiBlock = {
                        type: wireBlockTypeEnumToWireBlockTypeApiEnum.get(block.type.key as WireBlockTypeEnum),
                        ...companyBioDisplayElement.toApiModel()
                    } as WireCompanyBioApiModel;

                    apiBlocks.push(companyBioApiBlock);

                    break;
                default:
                    // Unknown block type, do nothing
                    break;
            }
        }
        apiModel.blocks = apiBlocks;
        return apiBlocks;
    }

    clone(): WireModel {
        const clone = new WireModel(this.toApiModel());

        return clone;
    }

    equals(other?: WireModel): boolean {
        if (!other) {
            return false;
        }

        // Compare simple fields
        for (let i = 0; i < this.simpleCompareFields.length; i++) {
            const field = this.simpleCompareFields[i];
            if (this[field] !== other[field]) {
                return false;
            }
        }

        // Order invariant equality
        for (const field of this.arrayCompareFields) {
            const left: any[] = this[field] ? this[field] : [];
            const right: any[] = other[field] ? other[field] : [];
            if (left.length === right.length) {
                for (const element of left) {
                    if (!right.find(x => x.equals(element))) {
                        return false;
                    }
                }
            } else {
                return false;
            }
        }

        // Compare Region
        if (!_.isEqual(this.regions, other.regions)) {
            return false;
        }

        // Compare seoKeywords
        if (!_.isEqual(this.seoKeywords, other.seoKeywords)) {
            return false;
        }

        // Compare companyAddress
        if (JSON.stringify(this.companyAddress) !== JSON.stringify(other.companyAddress)) {
            return false;
        }

        // Compare companyLogo
        if (JSON.stringify(this.companyLogo) !== JSON.stringify(other.companyLogo)) {
            return false;
        }

        // Compare blocks
        if (this.blocks.length !== other.blocks.length) {
            return false;
        }

        for (let i = 0; i < this.blocks.length; i++) {
            const thisBlock = this.blocks[i];
            const otherBlock = other.blocks[i];

            if (thisBlock.id !== otherBlock.id) {
                return false;
            }

            if (!thisBlock.type || !otherBlock.type || thisBlock.type.key !== otherBlock.type.key) {
                return false;
            }

            if (!thisBlock.displayElement.isEqual(otherBlock.displayElement)) {
                return false;
            }
        }

        // compare deliverySchedule
        if (!this.deliverySchedule.equals(other.deliverySchedule)) {
            return false;
        }

        return true;
    }

    constructDefault(): void {

    }
}
